import React, { useState } from "react";
import { Link } from "react-router-dom";
import { PostReportReason, POST_REPORT_REASONS } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";

import { apiService } from "src/services/api.service";
import { toast } from "src/components/Toast/ToastManager";
import Button from "src/components/Button/Button";
import TextArea from "src/components/TextArea/TextArea";

import styles from "./ReportPostModal.module.scss";

interface IProps {
    postId: string;
    onClose: () => void;
    /** Called after a successful report so the card can collapse. */
    onReported: () => void;
}

const NOTE_MAX = 500;
const REASON_LABEL = Constants.post_report_reason_label as Record<PostReportReason, string>;

const ReportPostModal: React.FC<IProps> = ({ postId, onClose, onReported }) => {
    const [reason, setReason] = useState<PostReportReason | null>(null);
    const [note, setNote] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const submit = async () => {
        if (!reason || submitting) return;
        setSubmitting(true);
        try {
            await apiService.reportPost(postId, { reason, note: note.trim() || undefined });
            toast.show({
                title: "Thanks — we'll take a look.",
                content: "This post won't show in your feed anymore.",
                duration: 6000,
                type: "success",
            });
            onReported();
            onClose();
        } catch {
            toast.show({
                title: "Couldn't send your report",
                content: "Please try again.",
                duration: 6000,
                type: "fail",
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className={styles.ReportPostModal}>
            <div className={styles.title}>Report this post</div>
            <p className={styles.subtitle}>
                Tell us what's wrong. The author won't see who reported it.
            </p>

            <div className={styles.reasons} role="radiogroup" aria-label="Reason">
                {POST_REPORT_REASONS.map((value) => {
                    const selected = reason === value;
                    return (
                        <button
                            key={value}
                            type="button"
                            role="radio"
                            aria-checked={selected}
                            className={`${styles.reason} ${selected ? styles.selected : ""}`}
                            onClick={() => setReason(value)}
                        >
                            <span className={`${styles.radio} ${selected ? styles.radioFilled : styles.radioEmpty}`} />
                            <span>{REASON_LABEL[value]}</span>
                        </button>
                    );
                })}
            </div>

            <div className={styles.noteField}>
                <TextArea
                    className={styles.noteInput}
                    label="Anything else we should know? (optional)"
                    placeholder=""
                    value={note}
                    onChange={(e) => setNote(e.target.value.slice(0, NOTE_MAX))}
                />
                <div className={styles.counter}>{note.length} / {NOTE_MAX}</div>
            </div>

            <p className={styles.guidelines}>
                See our <Link to="/community-guidelines" target="_blank" rel="noopener noreferrer">Community Guidelines</Link>.
            </p>

            <div className={styles.footer}>
                <Button variant="secondary" onClick={onClose} disabled={submitting}>Cancel</Button>
                <Button onClick={submit} disabled={!reason} isLoading={submitting}>Submit report</Button>
            </div>
        </div>
    );
};

export default ReportPostModal;
